import { isCancelNetworkError, NetworkError, requestInterceptor, responseInterceptor } from '@packages/dataFetch';
import Notifications from '../../../components/Notifications';
import { IS_PRODUCTION } from '../../../const/env';
import errorCodes from '../../../data/translations/errorCodes';
import { isObject } from '../../../predicates/type';
import { interceptorPageStatusCodes } from '../../customPage/error';
import { GenericErrorResponse } from '../network/types';
import { removePendingRequestConfig, tryToCancelEqualRequest } from '../network/utils';
import { detectSerializedFloatsWithComma } from './validation';
import { gaNetworkHandler } from '../../../components/GA/GANetwork/gaNetworkHandler';
import { interceptMaintenanceWork } from '../../../components/Maintenance/utils';
import { PUBLIC_CUSTOM_PAGE_PATH } from '../../../utils/const';

type Response302 = {
	redirect?: string;
	redirectMessage?: string;
};

export const skipRepeatMessagesEnum = {};

function hasMessageResponseData(message: string | undefined) {
	return typeof message === 'string' && message.length > 0;
}

export const interceptorStatusCodes = [302, 403, 440, 500, 502, 540];

const statusCodesList = {
	302: (error: NetworkError<Response302>) => {
		const redirect = error.response?.data?.redirect;
		const redirectMessage = error.response?.data?.redirectMessage;
		const redirectUrl = typeof redirect === 'string' && redirect.length > 0 ? redirect : '/';
		skipRepeatMessagesEnum['302'] = true;

		if (hasMessageResponseData(redirectMessage)) {
			Notifications.danger(redirectMessage);

			setTimeout(() => {
				window.location.href = redirectUrl;
			}, 1500);

			return;
		}

		window.location.href = redirectUrl;
	},
	403: (error: NetworkError<GenericErrorResponse>) => {
		const messageFromResponse = error.response?.data?.message;
		const message = hasMessageResponseData(messageFromResponse) ? messageFromResponse : error.message;

		Notifications.danger(message);
	},
	440: (error: NetworkError<GenericErrorResponse>) => {
		skipRepeatMessagesEnum['440'] = true;
		const messageFromResponse = error.response?.data?.message;
		const message = hasMessageResponseData(messageFromResponse)
			? messageFromResponse
			: window.reactTranslate(errorCodes.sessionDataExpired);

		Notifications.danger(message);
	},
	500: (error: NetworkError<GenericErrorResponse>) => {
		const messageFromResponse = error.response?.data?.message;
		const message = hasMessageResponseData(messageFromResponse)
			? messageFromResponse
			: window.reactTranslate(errorCodes.internalError);

		Notifications.danger(message);
	},
	502: (error: NetworkError<GenericErrorResponse>) => {
		const messageFromResponse = error.response?.data?.message;
		const message = hasMessageResponseData(messageFromResponse)
			? messageFromResponse
			: window.reactTranslate(errorCodes.internalError);

		Notifications.danger(message);
	},
	540: () => {
		skipRepeatMessagesEnum['540'] = true;
		window.location.href = PUBLIC_CUSTOM_PAGE_PATH.maintenance;
	},
};

export function initNetworkInterceptor() {
	requestInterceptor((config) => {
		tryToCancelEqualRequest(config.url, config.params);

		window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.push(config);

		if (!IS_PRODUCTION && isObject(config.data)) {
			detectSerializedFloatsWithComma(config.data);
		}

		return config;
	});

	responseInterceptor(
		(response) => {
			interceptMaintenanceWork();

			const redirect = response?.data?.redirect;

			if (typeof redirect !== 'undefined') {
				window.location.href = response.data.redirect;
			}

			removePendingRequestConfig(response.config);

			return response;
		},
		(error) => {
			const response = error.response;

			if (typeof response === 'undefined') {
				if (!window.navigator.onLine) {
					Notifications.danger('You are offline, please try again later.');
				}

				if (isCancelNetworkError(error)) {
					return Promise.reject(error);
				}

				return Promise.reject({
					response: {
						data: {},
					},
				});
			}

			const { status } = response;
			const skipFlag = skipRepeatMessagesEnum[status];
			const pageContentRequestHeader = error?.config?.headers?.PAGE_CONTENT_REQUEST_HEADER;
			const handleErrorByCustomPage: boolean =
				typeof pageContentRequestHeader === 'boolean' &&
				pageContentRequestHeader &&
				interceptorPageStatusCodes.includes(status);

			if (
				statusCodesList.hasOwnProperty(status) &&
				!handleErrorByCustomPage &&
				interceptorStatusCodes.includes(status) &&
				(typeof skipFlag === 'undefined' ||
					(typeof skipRepeatMessagesEnum[status] === 'boolean' && !skipRepeatMessagesEnum[status]))
			) {
				statusCodesList[status](error);
			}

			return Promise.reject(error);
		},
	);

	responseInterceptor((response) => {
		gaNetworkHandler(response);

		return response;
	});
}

export function shouldHandleErrorStatusCode(statusCode: unknown) {
	if (typeof statusCode === 'number') {
		return !interceptorStatusCodes.includes(statusCode as number);
	}

	return false;
}

export function notificationErrorMessageHandler(error: NetworkError) {
	if (interceptorStatusCodes.includes(error.response?.status!)) {
		return;
	}

	const message = error.response?.data?.message;

	if (typeof message === 'string' && message.length > 0) {
		Notifications.danger(message);

		return;
	}

	const messages = error.response?.data?.messages;

	if (typeof messages !== 'undefined' && Array.isArray(messages)) {
		messages.forEach((message) => {
			typeof message === 'string' && Notifications.danger(message);
		});

		return;
	}

	const description = error.response?.data?.description;

	if (typeof description === 'string' && description.length > 0) {
		Notifications.danger(description);

		return;
	}
}
